// Shared HTML sanitizer for distributor-authored rich text (the store
// "welcome message" / campaign.message column) before it is rendered via
// v-html on the storefront and the editor-side preview.
//
// WHY THIS EXISTS (stored-XSS boundary, CWE-79):
// campaign.message is distributor-authored HTML produced by the CKEditor5
// rich-text field in the store-editor Branding tab. CKEditor limits INPUT,
// but the persisted `message` column is craftable directly via the API,
// so any RENDER sink (v-html) must sanitize independently. Both message
// v-html sinks -- StorefrontViewShopper.vue (public shopper storefront) and
// StorefrontView.vue (editor-side preview) -- import this helper so they
// cannot drift: neither may render the raw column.
//
// Allowlist matches what the Branding CKEditor toolbar can emit
// (bold/italic, links, ordered/unordered lists, paragraphs, blockquotes)
// plus block-level structure CKEditor wraps content in, plus the inline
// `<span class="rte-font-*">` font mark (TipTap font picker -- a bounded
// class, NOT inline font-family CSS). DOMPurify strips
// <script>, event-handler attributes (onerror, onclick, ...), and
// dangerous URI schemes (javascript:, data:) from href/src.
import DOMPurify from "dompurify";

import {
  getImageBaseUrl,
  FALLBACK_IMAGE_BASE_URL,
} from "@/utils/imageBaseUrl";
import { RTE_FONT_CLASSES } from "@/utils/rteFonts";

// Tags the Branding rich-text editor can produce. Kept deliberately tight:
// text emphasis, links, lists, paragraphs, line breaks, block quotes, the
// headings the toolbar exposes, and (P3, CKEditor->TipTap migration) inline
// images whose `src` is host-restricted to our uploaded-asset origin below.
const ALLOWED_TAGS = [
  "p",
  "br",
  "b",
  "strong",
  "i",
  "em",
  "u",
  "s",
  "a",
  "ul",
  "ol",
  "li",
  "blockquote",
  "h2",
  "h3",
  "h4",
  "img",
  // Inline font mark (`<span class="rte-font-*">`) the editor's font picker
  // emits. The class is bounded by RTE_FONT_CLASSES in the attribute hook
  // below; a <span> with no surviving class is harmless (it renders as
  // plain inline text). Matches the backend allowlist, which already
  // permitted <span>.
  "span",
];

// Attributes allowed on the surviving tags. `href` on anchors, plus the
// safe anchor attributes CKEditor may add, plus the tight inline-image set
// (src additionally host-restricted by the hook below). `style` is allowed
// through so it REACHES the attribute hook -- which keeps it ONLY when it is a
// value-constrained `text-align` on a block tag, and drops it everywhere else
// (no distributor CSS injection into the storefront chrome). `class` is
// allowed through the SAME way -- the hook keeps it only on <span>, and only
// when EVERY token is a member of the closed RTE_FONT_CLASSES set. No id, no
// on* handlers.
const ALLOWED_ATTR = [
  "href",
  "target",
  "rel",
  "src",
  "alt",
  "width",
  "height",
  "style",
  "class",
];

// -- value-constrained text-align style (TipTap TextAlign extension) ---------
//
// The ONLY CSS we permit is `text-align: left|center|right` on the block tags
// TipTap targets (p, h2, h3, h4). This mirrors the backend sanitizer
// (api/src/utils/rich_text_sanitizer.py `_normalize_text_align_style`): the
// style value is kept ONLY when, after normalization, it EXACTLY equals one of
// the three allowed declarations (optional trailing `;` tolerated; casing +
// internal whitespace normalized). ANY other property, extra declaration,
// other value (incl. `justify`), or trailing junk drops the whole `style`
// attribute. Because DOMPurify's ALLOWED_ATTR is coarse (it cannot value-check
// a style), the uponSanitizeAttribute hook below is the sole gatekeeper.

// Block tags on which a value-constrained text-align style may live.
const TEXT_ALIGN_TAGS = new Set(["P", "H2", "H3", "H4"]);
// Only these three alignments (Jake excluded `justify`).
const ALLOWED_TEXT_ALIGN_VALUES = new Set(["left", "center", "right"]);
// Anchored (via .match on the full trimmed string + a length guard) exact
// `text-align: <value>` with optional single trailing `;`.
const TEXT_ALIGN_RE = /^text-align\s*:\s*([a-z]+)\s*;?$/i;

/**
 * Return the canonical `text-align: <value>` string iff `value` is exactly one
 * of our three allowed alignments (after normalization), else "" (drop it).
 *
 * @param {string} value - the raw style attribute value
 * @returns {string} canonical declaration, or "" when not allowed
 */
function normalizeTextAlignStyle(value) {
  if (!value || typeof value !== "string") return "";
  const m = value.trim().match(TEXT_ALIGN_RE);
  if (!m) return "";
  const align = m[1].toLowerCase();
  if (!ALLOWED_TEXT_ALIGN_VALUES.has(align)) return "";
  return `text-align: ${align}`;
}

// -- bounded font class (RichTextEditor font picker) ------------------------
//
// The ONLY classes we permit are the members of RTE_FONT_CLASSES (six
// self-hosted families), and ONLY on the inline <span> mark the editor emits.
// This mirrors the backend sanitizer (api/src/utils/rich_text_sanitizer.py
// `_normalize_font_class`).
//
// The check is ALL-OR-NOTHING per attribute: the value is split on
// whitespace and EVERY token must be an approved font class, else the whole
// `class` attribute is dropped. A partial keep would let an attacker smuggle
// `evil-injected` in beside a legitimate `rte-font-lora` and have it survive
// -- which matters because a storefront-scoped attacker class can borrow our
// existing app CSS (v-btn, material-icons, layout utilities) to re-style or
// obscure real chrome. Because DOMPurify's ALLOWED_ATTR is coarse (it cannot
// value-check a class), the uponSanitizeAttribute hook below is the sole
// gatekeeper -- exactly as it is for `style`.

// The single tag on which a font class may live (the inline mark).
const FONT_CLASS_TAGS = new Set(["SPAN"]);

/**
 * Return the canonical class value iff EVERY whitespace-separated token in
 * `value` is an approved font class, else "" (drop the whole attribute).
 * Whitespace is normalized and duplicate tokens collapse.
 *
 * @param {string} value - the raw class attribute value
 * @returns {string} canonical class list, or "" when not allowed
 */
function normalizeFontClass(value) {
  if (!value || typeof value !== "string") return "";
  const tokens = value.trim().split(/\s+/).filter(Boolean);
  if (tokens.length === 0) return "";
  // Every token must be approved -- one stranger drops the attribute.
  if (!tokens.every((t) => RTE_FONT_CLASSES.has(t))) return "";
  // Collapse duplicates, preserving first-seen order.
  return [...new Set(tokens)].join(" ");
}

// -- inline-image host restriction (mirror of the backend sanitizer) --------
//
// An <img src> is kept ONLY when it resolves to our uploaded-asset location:
// same origin as getImageBaseUrl() AND a path under getImageBaseUrl()'s path
// (which ends in "/images/"). Any remote host, protocol-relative URL,
// data:/javascript: scheme, userinfo-embedding URL, subdomain-suffix
// look-alike, or path-sibling ("/images-evil/") drops the src -- and an <img>
// with no src is dropped by DOMPurify. This is a URL()-based compare (NOT a
// bare string.startsWith), matching api/src/utils/rich_text_sanitizer.py.
//
// FAIL CLOSED (condition 5): when the build did not inline S3_IMAGE_BASE_URL,
// getImageBaseUrl() returns the same-origin FALLBACK_IMAGE_BASE_URL ("/images/").
// We reject ALL img src in that case rather than allow a weak same-origin
// "/images/" that a mis-deploy could turn into an attacker-asset sink.

/**
 * True iff `src` resolves to our uploaded-asset origin+path.
 *
 * @param {string} src - the candidate img src attribute value
 * @returns {boolean}
 */
function isOurAssetSrc(src) {
  if (!src || typeof src !== "string") return false;

  const base = getImageBaseUrl();
  // Fail closed: an unconfigured build (fallback base) allows NO images.
  if (base === FALLBACK_IMAGE_BASE_URL) return false;

  let assetBase;
  let candidate;
  try {
    // Resolve the asset base against the page origin (the base is normally
    // absolute; resolving is a no-op then, and gives a stable origin when
    // it is relative). Resolve the candidate the SAME way so a relative or
    // protocol-relative src is pinned to a concrete origin before compare.
    assetBase = new URL(base, window.location.origin);
    candidate = new URL(src, window.location.origin);
  } catch {
    return false;
  }

  // Explicit scheme guard: only http/https srcs are eligible. This drops
  // data:image/svg+xml (which DOMPurify KEEPS on img by default) and any
  // javascript:/blob:/etc. scheme regardless of the origin compare.
  if (candidate.protocol !== "http:" && candidate.protocol !== "https:") {
    return false;
  }

  // Origin must match EXACTLY (scheme + host + port). URL.origin folds
  // userinfo out and normalizes host, so `host@evil.com` and `host.evil.com`
  // both fail here.
  if (candidate.origin !== assetBase.origin) return false;

  // Path must live under the asset base path (which ends in "/images/"), so
  // a sibling like "/images-evil/..." cannot pass. Normalize the base path to
  // guarantee the trailing slash even if a future base loses it.
  const basePath = assetBase.pathname.endsWith("/")
    ? assetBase.pathname
    : assetBase.pathname + "/";
  return candidate.pathname.startsWith(basePath);
}

// DOMPurify hook entry point. We police <img> at the ELEMENT level: dropping
// just the src attribute is NOT enough -- DOMPurify keeps a src-less <img>,
// which then renders nothing but leaves an empty element on the page. So a
// non-our-origin <img> is REMOVED WHOLESALE. Registered/deregistered around
// each sanitize call so the policy never leaks into other DOMPurify callers.
const ELEMENT_HOOK = "uponSanitizeElement";
const ATTRIBUTE_HOOK = "uponSanitizeAttribute";

/**
 * uponSanitizeAttribute hook: value-constrain the two attributes DOMPurify's
 * coarse ALLOWED_ATTR cannot police by value.
 *
 * `style` is kept ONLY when it is a valid `text-align: left|center|right` on a
 * block tag (p/h2/h3/h4); the value is rewritten to its canonical form.
 *
 * `class` is kept ONLY on the inline <span> font mark, and only when EVERY
 * token is a member of the closed RTE_FONT_CLASSES set.
 *
 * On any other tag, or any non-conforming value, the attribute is dropped
 * (element kept). Every other attribute is left untouched (DOMPurify's
 * ALLOWED_ATTR already gated it). Registered/deregistered per sanitize call so
 * the policy never leaks into other DOMPurify callers.
 */
function styleAttributeHook(node, data) {
  if (!data) return;
  const tag = node && node.tagName;

  if (data.attrName === "class") {
    if (!FONT_CLASS_TAGS.has(tag)) {
      // class is not permitted on this tag -- drop it.
      data.keepAttr = false;
      return;
    }
    const canonicalClass = normalizeFontClass(data.attrValue);
    if (!canonicalClass) {
      data.keepAttr = false;
      return;
    }
    data.attrValue = canonicalClass;
    data.keepAttr = true;
    return;
  }

  if (data.attrName !== "style") return;
  if (!TEXT_ALIGN_TAGS.has(tag)) {
    // style is not permitted on this tag -- drop it.
    data.keepAttr = false;
    return;
  }
  const canonical = normalizeTextAlignStyle(data.attrValue);
  if (!canonical) {
    data.keepAttr = false;
    return;
  }
  // Keep it, rewritten to the canonical declaration.
  data.attrValue = canonical;
  data.keepAttr = true;
}

/**
 * uponSanitizeElement hook: remove any <img> whose src is not one of our
 * uploaded assets (remote host, protocol-relative, data:/javascript:,
 * userinfo, subdomain-suffix, path-sibling, or -- fail closed -- an
 * unconfigured asset base). Reads the raw `src` attribute BEFORE DOMPurify's
 * own attribute pass so a bad scheme is caught here regardless.
 */
function imgElementHook(node) {
  if (!node || node.tagName !== "IMG") return;
  const src =
    typeof node.getAttribute === "function" ? node.getAttribute("src") : null;
  if (!isOurAssetSrc(src)) {
    // Remove the whole element -- the documented DOMPurify hook pattern for
    // dropping a node wholesale (a src-less <img> would otherwise survive).
    if (node.parentNode) {
      node.parentNode.removeChild(node);
    } else if (typeof node.remove === "function") {
      node.remove();
    }
  }
}

/**
 * Sanitize distributor-authored HTML for safe v-html rendering.
 *
 * Strips scripts, event handlers, and dangerous URI schemes; keeps a tight
 * allowlist of formatting tags plus host-restricted inline images. Returns ""
 * for null/undefined/empty input.
 *
 * @param {string} dirty - untrusted HTML string (campaign.message / rich_text)
 * @returns {string} sanitized HTML safe to bind via v-html
 */
export function sanitizeMessageHtml(dirty) {
  if (!dirty || typeof dirty !== "string") return "";
  // Register the img element hook + the value-constrained style attribute
  // hook for THIS call only, then remove them, so the policy is self-contained
  // and cannot affect other sanitize callers.
  DOMPurify.addHook(ELEMENT_HOOK, imgElementHook);
  DOMPurify.addHook(ATTRIBUTE_HOOK, styleAttributeHook);
  try {
    return DOMPurify.sanitize(dirty, {
      ALLOWED_TAGS,
      ALLOWED_ATTR,
      // Belt-and-suspenders: block javascript:/data: even if allowed on an
      // attribute. DOMPurify already blocks these by default for href/src,
      // and the img hook independently rejects any non-our-origin src.
      ALLOW_UNKNOWN_PROTOCOLS: false,
    });
  } finally {
    // Remove exactly the hooks we added (pass the function refs) so a hook
    // some other caller registered concurrently is never popped by mistake.
    DOMPurify.removeHook(ELEMENT_HOOK, imgElementHook);
    DOMPurify.removeHook(ATTRIBUTE_HOOK, styleAttributeHook);
  }
}

// -- Store POLICY pages (Privacy / Refund) ----------------------------------
//
// A policy page is legal copy shown to every shopper of a store at
// /shop/<slug>/privacy-policy | /refund-policy. Its allowlist is NARROWER than
// the welcome message's: no images at all (not even from our asset origin),
// no <h1> (the page title is the h1), and links may only be http(s) or
// mailto. A horizontal rule is allowed (legal copy separates sections).
// Mirrors api/src/utils/rich_text_sanitizer.py `sanitize_policy_html`; the
// API already sanitized on write and on read, so this is the third pass.
const POLICY_ALLOWED_TAGS = [
  "p",
  "br",
  "hr",
  "b",
  "strong",
  "i",
  "em",
  "u",
  "a",
  "ul",
  "ol",
  "li",
  "blockquote",
  "h2",
  "h3",
  "h4",
  "span",
];
const POLICY_ALLOWED_ATTR = ["href", "target", "rel", "style", "class"];
// Only full http(s) and mailto URLs: no tel:, ftp:, relative or
// protocol-relative links.
const POLICY_ALLOWED_URI = /^(?:https?|mailto):/i;
const POLICY_LINK_REL = "noopener noreferrer nofollow";
const AFTER_ATTRIBUTES_HOOK = "afterSanitizeAttributes";

/**
 * afterSanitizeAttributes hook for policy pages: every anchor gets the safe
 * rel (overwriting whatever the author wrote), and `target` survives only as
 * `_blank`.
 */
function policyLinkHook(node) {
  if (!node || node.tagName !== "A") return;
  node.setAttribute("rel", POLICY_LINK_REL);
  if (node.hasAttribute("target") && node.getAttribute("target") !== "_blank") {
    node.removeAttribute("target");
  }
}

/**
 * Sanitize distributor-authored policy-page HTML for v-html rendering.
 * Returns "" for null / empty / non-string input.
 *
 * @param {string} dirty - untrusted HTML (the policy body)
 * @returns {string} sanitized HTML
 */
export function sanitizePolicyHtml(dirty) {
  if (!dirty || typeof dirty !== "string") return "";
  // Same value-constrained style / class hook as the welcome message, plus
  // the anchor hook. Registered for THIS call only so neither leaks into
  // other DOMPurify callers.
  DOMPurify.addHook(ATTRIBUTE_HOOK, styleAttributeHook);
  DOMPurify.addHook(AFTER_ATTRIBUTES_HOOK, policyLinkHook);
  try {
    return DOMPurify.sanitize(dirty, {
      ALLOWED_TAGS: POLICY_ALLOWED_TAGS,
      ALLOWED_ATTR: POLICY_ALLOWED_ATTR,
      ALLOWED_URI_REGEXP: POLICY_ALLOWED_URI,
      ALLOW_UNKNOWN_PROTOCOLS: false,
      ALLOW_DATA_ATTR: false,
    });
  } finally {
    DOMPurify.removeHook(ATTRIBUTE_HOOK, styleAttributeHook);
    DOMPurify.removeHook(AFTER_ATTRIBUTES_HOOK, policyLinkHook);
  }
}

export default sanitizeMessageHtml;
